// Advanced Graphs — Basic vs Optimized code for every "Beginner Problems" drill listed in the
// Advanced Graphs Explanation modal. Same shape as graphDrills.js: concise steps + an
// interactive "watch it happen" visualizer per version, reusing ApproachVisualizer's
// (now weight-aware) graph-rendering support. One drill cross-references the curated problem.

const advancedGraphDrills = [
  {
    id: 'weighted-adjacency-list',
    title: 'Represent a Weighted Graph',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Weighted Adjacency Matrix',
        explanation: 'A V×V grid where matrix[i][j] holds the weight of the edge from i to j (or a sentinel like -1 / infinity if no edge exists). Simple, and looking up a specific edge\'s weight is instant — but the grid always costs V² space, regardless of how many edges actually exist.',
        steps: [
          'Allocate a V×V int grid, filled with a "no edge" sentinel (e.g. -1).',
          'For each weighted edge (a, b, w), set matrix[a][b] = w (and matrix[b][a] = w, if undirected).',
          'To look up an edge\'s weight, read matrix[a][b] directly — O(1).',
          'To list a node\'s neighbors and their weights, scan its entire row — O(V), even for a node with 1 neighbor.',
        ],
        code:
`int[][] buildWeightedMatrix(int v, int[][] edges) {
    int[][] matrix = new int[v][v];
    for (int[] row : matrix) Arrays.fill(row, -1);
    for (int[] edge : edges) {
        matrix[edge[0]][edge[1]] = edge[2];
        matrix[edge[1]][edge[0]] = edge[2];   // omit for a directed graph
    }
    return matrix;
}`,
        complexity: { time: 'O(1) edge-weight lookup', space: 'O(V²) — always' },
      },
      {
        name: 'Optimized — Weighted Adjacency List',
        explanation: 'For each node, keep a list of [neighbor, weight] pairs — nothing stored for pairs that aren\'t connected. This is exactly what Dijkstra, Prim\'s, and Bellman-Ford all expect as input.',
        steps: [
          'Keep a map from each node to a list of [neighbor, weight] pairs.',
          'For each weighted edge (a, b, w), append [b, w] to a\'s list (and [a, w] to b\'s list, if undirected).',
          'To list a node\'s neighbors and weights, read its list directly.',
          'Space scales with the number of edges actually present, not the square of the node count.',
        ],
        code:
`Map<Integer, List<int[]>> buildWeightedAdjacencyList(int v, int[][] edges) {
    Map<Integer, List<int[]>> adj = new HashMap<>();
    for (int i = 0; i < v; i++) adj.put(i, new ArrayList<>());
    for (int[] edge : edges) {
        adj.get(edge[0]).add(new int[] { edge[1], edge[2] });
        adj.get(edge[1]).add(new int[] { edge[0], edge[2] });   // omit for a directed graph
    }
    return adj;
}`,
        complexity: { time: 'O(degree) to list a node\'s weighted neighbors', space: 'O(V + E)' },
      },
    ],
  },
  {
    id: 'shortest-path-unweighted-bfs',
    title: 'Shortest Path in an Unweighted Graph',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — DFS, Tracking the Minimum Path Length Found',
        explanation: 'Explore every possible path from the source to the target using DFS, recording the length of each complete path found, and keep the smallest. Correct, but this explores every path — including many far longer than necessary — before the shortest is confirmed.',
        steps: [
          'DFS from the source, tracking the current path length.',
          'Whenever the target is reached, compare this path\'s length to the best found so far.',
          'Continue exploring — DFS doesn\'t stop at the first path found, since a shorter one might exist elsewhere.',
          'Every possible path gets explored, even ones obviously longer than the current best.',
        ],
        code:
`int shortestPathBasic(int src, int dst, Map<Integer, List<Integer>> adj) {
    int[] best = { Integer.MAX_VALUE };
    dfs(src, dst, 0, new HashSet<>(), adj, best);
    return best[0] == Integer.MAX_VALUE ? -1 : best[0];
}

private void dfs(int node, int dst, int length, Set<Integer> visited, Map<Integer, List<Integer>> adj, int[] best) {
    if (node == dst) { best[0] = Math.min(best[0], length); return; }
    visited.add(node);
    for (int neighbor : adj.get(node)) {
        if (!visited.contains(neighbor)) dfs(neighbor, dst, length + 1, visited, adj, best);
    }
    visited.remove(node);
}`,
        complexity: { time: 'Exponential — explores every simple path', space: 'O(V) — recursion depth' },
      },
      {
        name: 'Optimized — BFS',
        explanation: 'Since every edge costs the same (1 "hop"), BFS discovers nodes in STRICT order of distance from the source — the first time the target is reached is guaranteed to be via the shortest possible path. No need to explore further, and no need to compare path lengths at all.',
        steps: [
          'BFS from the source, tracking each node\'s distance as it\'s first discovered.',
          'The moment the target is dequeued, its recorded distance IS the shortest path length.',
          'Return immediately — every unexplored node is guaranteed to be at least as far as the target.',
          'Each node is visited exactly once — no redundant path exploration.',
        ],
        visual: {
          graph: { label: 'graph', nodes: [0, 1, 2, 3], edges: [[0, 1], [0, 2], [1, 3], [2, 3]], directed: false },
          varNames: ['distance'],
          steps: [
            { highlights: { graph: [0] }, vars: { distance: 0 }, note: 'Start at 0, distance 0.' },
            { highlights: { graph: [1, 2] }, vars: { distance: 1 }, note: 'Discover 1 and 2 — both distance 1.' },
            { highlights: { graph: [3] }, vars: { distance: 2 }, note: 'Discover 3 (via either 1 or 2) — distance 2. Target reached — shortest path length: 2, found without exploring any further.' },
          ],
        },
        code:
`int shortestPath(int src, int dst, Map<Integer, List<Integer>> adj) {
    Map<Integer, Integer> dist = new HashMap<>();
    dist.put(src, 0);
    Queue<Integer> queue = new LinkedList<>();
    queue.add(src);

    while (!queue.isEmpty()) {
        int node = queue.poll();
        if (node == dst) return dist.get(node);
        for (int neighbor : adj.get(node)) {
            if (!dist.containsKey(neighbor)) {
                dist.put(neighbor, dist.get(node) + 1);
                queue.add(neighbor);
            }
        }
    }
    return -1;   // unreachable
}`,
        complexity: { time: 'O(V + E)', space: 'O(V)' },
        interviewNote: "This is exactly why Dijkstra's algorithm — covered later in this pattern — reduces to plain BFS when every edge weight is 1: the priority queue's ordering and BFS's queue ordering become the same thing.",
      },
    ],
  },
  {
    id: 'network-delay-time-drill',
    title: 'Network Delay Time',
    level: 'Easy',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Network Delay Time\" there for the complete step-by-step and both code versions. You can also try " +
      "Dijkstra's algorithm hands-on in this pattern's Interactive Practice playground with any graph you build.",
  },
  {
    id: 'dijkstra-from-scratch',
    title: "Dijkstra's Algorithm From Scratch",
    level: 'Medium',
    versions: [
      {
        name: 'Basic — O(V²) Array Scan',
        explanation: 'At each of V steps, scan every node with a plain loop to find the unvisited one with the smallest known distance. Correct — this is literally the original textbook version of Dijkstra\'s algorithm — but that linear scan happens V times, whether or not a faster structure is available.',
        steps: [
          'Initialize distances: 0 for the source, infinity for everything else.',
          'Repeat V times: scan every unvisited node with a plain loop to find the one with the smallest distance.',
          'Mark it visited, and relax every edge out of it (update a neighbor\'s distance if going through this node is shorter).',
          'The linear scan for "smallest unvisited distance" costs O(V) every single time, for O(V²) total.',
        ],
        code:
`int[] dijkstraBasic(int src, int n, Map<Integer, List<int[]>> adj) {
    int[] dist = new int[n];
    Arrays.fill(dist, Integer.MAX_VALUE);
    dist[src] = 0;
    boolean[] visited = new boolean[n];

    for (int i = 0; i < n; i++) {
        int node = -1, best = Integer.MAX_VALUE;
        for (int j = 0; j < n; j++) {           // O(V) scan, every iteration
            if (!visited[j] && dist[j] < best) { best = dist[j]; node = j; }
        }
        if (node == -1) break;
        visited[node] = true;
        for (int[] edge : adj.get(node)) {
            int neighbor = edge[0], weight = edge[1];
            if (dist[node] + weight < dist[neighbor]) {
                dist[neighbor] = dist[node] + weight;
            }
        }
    }
    return dist;
}`,
        complexity: { time: 'O(V²)', space: 'O(V)' },
        whenToUse: "Perfectly fine (and simple to write correctly under pressure) for small or DENSE graphs, where V² isn't much worse than the heap version anyway.",
      },
      {
        name: 'Optimized — Min-Heap (Priority Queue)',
        explanation: 'Instead of scanning every node to find the current minimum, keep a min-heap of (distance, node) pairs — the heap always hands back the smallest distance in O(log V) instead of O(V).',
        steps: [
          'Initialize distances the same way, and push (0, source) onto a min-heap.',
          'Repeatedly pop the smallest-distance entry. If it\'s stale (a better distance was already found), skip it.',
          'Otherwise, relax every outgoing edge — for any improvement, push the new (distance, neighbor) pair onto the heap.',
          'The heap replaces the O(V) linear scan with an O(log V) pop, at the cost of possibly holding some stale entries (handled by the skip check).',
        ],
        visual: {
          graph: { label: 'graph', nodes: [0, 1, 2, 3], edges: [[0, 1, 2], [0, 2, 4], [1, 2, 1], [1, 3, 7], [2, 3, 3]], directed: false },
          varNames: [],
          steps: [
            { highlights: { graph: [0] }, note: 'Pop (0, node 0) — the smallest. Relax edges: dist[1]=2, dist[2]=4. Push both onto the heap.' },
            { highlights: { graph: [1] }, note: 'Pop (2, node 1) — currently smallest in the heap. Relax: dist[2] = min(4, 2+1=3) = 3 (improved!). dist[3] = 2+7 = 9. Push both.' },
            { highlights: { graph: [2] }, note: 'Pop (3, node 2) — smallest remaining. Relax: dist[3] = min(9, 3+3=6) = 6 (improved!). Push it.' },
            { highlights: { graph: [3] }, note: 'Pop (6, node 3). No unvisited neighbors to relax further. Final distances: [0, 2, 3, 6].' },
          ],
        },
        code:
`int[] dijkstra(int src, int n, Map<Integer, List<int[]>> adj) {
    int[] dist = new int[n];
    Arrays.fill(dist, Integer.MAX_VALUE);
    dist[src] = 0;
    PriorityQueue<int[]> pq = new PriorityQueue<>((a, b) -> a[1] - b[1]);   // {node, distance}
    pq.add(new int[] { src, 0 });

    while (!pq.isEmpty()) {
        int[] top = pq.poll();
        int node = top[0], d = top[1];
        if (d > dist[node]) continue;   // stale entry — a better distance was already found

        for (int[] edge : adj.get(node)) {
            int neighbor = edge[0], weight = edge[1];
            if (dist[node] + weight < dist[neighbor]) {
                dist[neighbor] = dist[node] + weight;
                pq.add(new int[] { neighbor, dist[neighbor] });
            }
        }
    }
    return dist;
}`,
        complexity: { time: 'O((V + E) log V)', space: 'O(V + E)' },
        whenToUse: "The standard answer for sparse graphs — which is most graphs in practice — since (V+E)log V beats V² once E is meaningfully smaller than V².",
      },
    ],
  },
  {
    id: 'minimum-spanning-tree-prims',
    title: "Minimum Spanning Tree: Prim's Algorithm",
    level: 'Medium',
    versions: [
      {
        name: 'Basic — O(V²) Array Scan',
        explanation: 'Grow the MST from a starting node, and at each step, scan every edge crossing the current frontier (from an in-MST node to an out-of-MST node) with a plain loop to find the cheapest one. Correct, but the linear scan repeats V times.',
        steps: [
          'Start the MST with just one node.',
          'Repeat until every node is included: scan all edges crossing the current frontier for the cheapest one.',
          'Add that edge\'s far endpoint to the MST.',
          'The frontier scan costs O(V) or O(E) every iteration, without a structure to shortcut it.',
        ],
        code:
`int primsBasic(int n, Map<Integer, List<int[]>> adj) {
    boolean[] inMST = new boolean[n];
    inMST[0] = true;
    int totalWeight = 0;

    for (int i = 1; i < n; i++) {
        int bestNode = -1, bestWeight = Integer.MAX_VALUE, bestFrom = -1;
        for (int node = 0; node < n; node++) {
            if (!inMST[node]) continue;
            for (int[] edge : adj.get(node)) {
                if (!inMST[edge[0]] && edge[1] < bestWeight) {
                    bestWeight = edge[1]; bestNode = edge[0]; bestFrom = node;
                }
            }
        }
        if (bestNode == -1) break;
        inMST[bestNode] = true;
        totalWeight += bestWeight;
    }
    return totalWeight;
}`,
        complexity: { time: 'O(V²)', space: 'O(V)' },
      },
      {
        name: 'Optimized — Min-Heap of Frontier Edges',
        explanation: 'Keep every edge currently crossing the frontier in a min-heap, ordered by weight. Popping the cheapest is O(log E) instead of an O(V) or O(E) scan.',
        steps: [
          'Start with one node in the MST, and push all of its outgoing edges onto a min-heap.',
          'Pop the cheapest edge. If its far endpoint is already in the MST, discard it (stale) and pop again.',
          'Otherwise, add that endpoint to the MST, and push all of ITS outgoing edges onto the heap.',
          'Repeat until every node is included.',
        ],
        visual: {
          graph: { label: 'graph', nodes: [0, 1, 2, 3], edges: [[0, 1, 1], [0, 2, 4], [1, 2, 2], [1, 3, 5], [2, 3, 3]], directed: false },
          varNames: ['totalWeight'],
          steps: [
            { highlights: { graph: [0] }, vars: { totalWeight: 0 }, note: 'Start at 0. Push its edges: (0-1, 1), (0-2, 4).' },
            { highlights: { graph: [0, 1] }, vars: { totalWeight: 1 }, note: 'Pop cheapest: (0-1, 1). Add 1. Push 1\'s edges: (1-2, 2), (1-3, 5).' },
            { highlights: { graph: [1, 2] }, vars: { totalWeight: 3 }, note: 'Pop cheapest: (1-2, 2). Add 2. Push 2\'s new edge: (2-3, 3). (0-2, 4) is now stale — 2 is already in.' },
            { highlights: { graph: [2, 3] }, vars: { totalWeight: 6 }, note: 'Pop cheapest: (2-3, 3), beating the stale (1-3, 5). Add 3. All nodes in — MST complete, total weight 6.' },
          ],
        },
        code:
`int prims(int n, Map<Integer, List<int[]>> adj) {
    boolean[] inMST = new boolean[n];
    PriorityQueue<int[]> pq = new PriorityQueue<>((a, b) -> a[1] - b[1]);   // {node, weight}
    pq.add(new int[] { 0, 0 });
    int totalWeight = 0, count = 0;

    while (!pq.isEmpty() && count < n) {
        int[] top = pq.poll();
        int node = top[0], weight = top[1];
        if (inMST[node]) continue;   // stale — already added via a cheaper edge
        inMST[node] = true;
        totalWeight += weight;
        count++;
        for (int[] edge : adj.get(node)) {
            if (!inMST[edge[0]]) pq.add(edge);
        }
    }
    return totalWeight;
}`,
        complexity: { time: 'O(E log E)', space: 'O(E)' },
        whenToUse: "Reach for Prim's when the graph is dense (many edges relative to nodes) — it grows outward from a single point, which fits naturally with an adjacency-list-and-heap implementation.",
      },
    ],
  },
  {
    id: 'minimum-spanning-tree-kruskals',
    title: "Minimum Spanning Tree: Kruskal's Algorithm",
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Naive Cycle Check via Reachability',
        explanation: 'Sort every edge by weight. Walk through them in order, and for each one, run a full BFS/DFS to check whether its two endpoints are already connected in the MST-so-far — if not, add the edge. Correct, but a full connectivity check for every candidate edge is expensive.',
        steps: [
          'Sort all edges by weight, ascending.',
          'For each edge, run a BFS/DFS over the MST-so-far to check if its two endpoints are already connected.',
          'If not connected, add the edge to the MST.',
          'If already connected, adding it would create a cycle — skip it. Each check costs O(V + E).',
        ],
        code:
`int kruskalsBasic(int n, int[][] edges) {
    Arrays.sort(edges, (a, b) -> a[2] - b[2]);
    List<int[]> mst = new ArrayList<>();
    int totalWeight = 0;

    for (int[] edge : edges) {
        if (!connected(mst, edge[0], edge[1], n)) {   // full BFS/DFS check, every edge
            mst.add(edge);
            totalWeight += edge[2];
        }
    }
    return totalWeight;
}
// connected(mst, a, b, n): builds an adjacency list from the current mst edges,
// then BFS/DFS-checks whether a can reach b. Omitted for brevity.`,
        complexity: { time: 'O(E · (V + E))', space: 'O(V + E)' },
      },
      {
        name: 'Optimized — Union-Find',
        explanation: 'Instead of a full connectivity check per edge, use Union-Find: "are these two nodes already connected?" becomes a near-O(1) find() call instead of a graph traversal.',
        steps: [
          'Sort all edges by weight, ascending. Start every node in its own component.',
          'For each edge, find() the roots of both endpoints.',
          'If the roots differ, union them and add the edge to the MST.',
          'If the roots are the same, this edge would create a cycle — skip it.',
        ],
        visual: {
          graph: { label: 'graph', nodes: [0, 1, 2, 3], edges: [[0, 1, 1], [0, 2, 4], [1, 2, 2], [1, 3, 5], [2, 3, 3]], directed: false },
          varNames: ['totalWeight'],
          steps: [
            { highlights: { graph: [0, 1] }, highlightEdges: [[0, 1]], vars: { totalWeight: 1 }, note: 'Sorted order: (0-1,1) first. find(0)≠find(1) — union, add. Total: 1.' },
            { highlights: { graph: [1, 2] }, highlightEdges: [[1, 2]], vars: { totalWeight: 3 }, note: '(1-2,2) next. find(1)≠find(2) — union, add. Total: 3.' },
            { highlights: { graph: [2, 3] }, highlightEdges: [[2, 3]], vars: { totalWeight: 6 }, note: '(2-3,3) next. find(2)≠find(3) — union, add. Total: 6. All 4 nodes connected — MST complete.' },
            { note: '(0-2,4) and (1-3,5) are never even reached — count(3 edges for 4 nodes) already confirms the MST is done.' },
          ],
        },
        code:
`int kruskals(int n, int[][] edges) {
    Arrays.sort(edges, (a, b) -> a[2] - b[2]);
    int[] parent = new int[n];
    for (int i = 0; i < n; i++) parent[i] = i;

    int totalWeight = 0, edgesUsed = 0;
    for (int[] edge : edges) {
        int rootA = find(parent, edge[0]), rootB = find(parent, edge[1]);
        if (rootA != rootB) {
            parent[rootA] = rootB;
            totalWeight += edge[2];
            edgesUsed++;
            if (edgesUsed == n - 1) break;   // MST complete — no need to check remaining edges
        }
    }
    return totalWeight;
}

private int find(int[] parent, int x) {
    if (parent[x] != x) parent[x] = find(parent, parent[x]);
    return parent[x];
}`,
        complexity: { time: 'O(E log E) — dominated by the sort', space: 'O(V + E)' },
        whenToUse: "Reach for Kruskal's when the graph is sparse, or when edges are naturally given as a flat list rather than an adjacency structure — sorting them once and processing in order is simpler to reason about than growing a frontier.",
      },
    ],
  },
  {
    id: 'cheapest-flights-k-stops',
    title: 'Cheapest Flights Within K Stops',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Explore Every Path Up to K+1 Flights',
        explanation: 'DFS/BFS through every possible sequence of flights, stopping any path once it exceeds k stops, and track the cheapest complete path found. Correct, but the branching factor makes this explode quickly.',
        steps: [
          'Explore every path from the source, tracking cumulative cost and stops used.',
          'Stop extending a path once it would exceed k stops (k+1 flights).',
          'Whenever the destination is reached within the stop limit, compare its cost to the best found.',
          'Every branch of every possible route gets explored — exponential in the worst case.',
        ],
        code:
`int findCheapestPriceBasic(int n, int[][] flights, int src, int dst, int k) {
    Map<Integer, List<int[]>> adj = new HashMap<>();
    for (int[] f : flights) adj.computeIfAbsent(f[0], x -> new ArrayList<>()).add(new int[]{f[1], f[2]});
    int[] best = { Integer.MAX_VALUE };
    dfs(src, dst, k + 1, 0, adj, best);
    return best[0] == Integer.MAX_VALUE ? -1 : best[0];
}

private void dfs(int node, int dst, int flightsLeft, int cost, Map<Integer, List<int[]>> adj, int[] best) {
    if (node == dst) { best[0] = Math.min(best[0], cost); return; }
    if (flightsLeft == 0) return;
    for (int[] edge : adj.getOrDefault(node, List.of())) {
        dfs(edge[0], dst, flightsLeft - 1, cost + edge[1], adj, best);
    }
}`,
        complexity: { time: 'Exponential in k', space: 'O(k) — recursion depth' },
      },
      {
        name: 'Optimized — Bellman-Ford, Limited to K+1 Rounds',
        explanation: 'Run Bellman-Ford-style edge relaxation, but STOP after exactly k+1 rounds — one round per allowed flight. Crucially, relax using a SNAPSHOT of the previous round\'s distances (not values already updated this round), so no path accidentally uses more than the allowed number of flights.',
        steps: [
          'Initialize distances: 0 for the source, infinity elsewhere.',
          'Repeat exactly k+1 times (one round per allowed flight): take a snapshot of the current distances.',
          'Relax every flight using ONLY the snapshot values — never a value already updated in this same round.',
          'After k+1 rounds, dist[destination] holds the cheapest cost achievable in at most k+1 flights.',
        ],
        visual: {
          graph: { label: 'flights (k = 1 stop, so 2 flights allowed)', nodes: [0, 1, 2, 3], edges: [[0, 1, 100], [1, 2, 100], [2, 0, 100], [1, 3, 600], [2, 3, 200]], directed: true },
          varNames: [],
          steps: [
            { highlights: { graph: [0, 1] }, note: 'Round 1 (flight #1): using the snapshot (only node 0 = 0, everything else ∞), relax 0→1 (100). dist[1] = 100. Every other edge starts from ∞ — no other change.' },
            { highlights: { graph: [1, 2] }, note: 'Round 2 (flight #2, the last allowed): snapshot is now [0, 100, ∞, ∞]. Relax 1→2 (100): dist[2] = 100+100 = 200. Relax 1→3 (600): dist[3] = 100+600 = 700.' },
            { note: 'k+1 = 2 rounds complete — stop here. dist[3] = 700 is the answer. (0→1→2→3 would need 3 flights — exceeding k=1 stop — so it\'s correctly never considered.)' },
          ],
        },
        code:
`int findCheapestPrice(int n, int[][] flights, int src, int dst, int k) {
    int[] dist = new int[n];
    Arrays.fill(dist, Integer.MAX_VALUE);
    dist[src] = 0;

    for (int round = 0; round <= k; round++) {
        int[] snapshot = dist.clone();   // freeze last round's values
        for (int[] flight : flights) {
            int from = flight[0], to = flight[1], price = flight[2];
            if (snapshot[from] != Integer.MAX_VALUE && snapshot[from] + price < dist[to]) {
                dist[to] = snapshot[from] + price;
            }
        }
    }
    return dist[dst] == Integer.MAX_VALUE ? -1 : dist[dst];
}`,
        complexity: { time: 'O(k · E)', space: 'O(V)' },
        interviewNote: "The snapshot is the subtle, easy-to-miss detail: relaxing directly against the live dist[] array (instead of a frozen snapshot) can let a single round accidentally chain two flights together, silently violating the k-stops limit.",
        whenToUse: "Whenever a shortest-path problem adds an extra constraint on the NUMBER OF EDGES used (not just total weight), bounding Bellman-Ford's round count to match that limit is the standard technique — Dijkstra has no clean way to enforce an edge-count cap.",
      },
    ],
  },
  {
    id: 'floyd-warshall-all-pairs',
    title: 'All-Pairs Shortest Path (Floyd-Warshall)',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Run Dijkstra From Every Node',
        explanation: 'To find the shortest path between EVERY pair of nodes, just run Dijkstra\'s algorithm once per starting node. Correct, and each individual run is efficient — but V separate runs adds up, and this approach doesn\'t work at all if any edge weight is negative.',
        steps: [
          'For each node in the graph, run Dijkstra\'s algorithm with it as the source.',
          'Record the resulting distance array as one row of the final all-pairs distance table.',
          'Repeat for every node.',
          'V runs of an O((V+E) log V) algorithm — and none of it is valid if a negative edge weight exists anywhere.',
        ],
        code:
`int[][] allPairsBasic(int n, Map<Integer, List<int[]>> adj) {
    int[][] result = new int[n][];
    for (int src = 0; src < n; src++) {
        result[src] = dijkstra(src, n, adj);   // the standard Dijkstra from earlier in this pattern
    }
    return result;
}`,
        complexity: { time: 'O(V · (V + E) log V)', space: 'O(V²)', },
        whenToUse: "Only valid when every edge weight is non-negative — Floyd-Warshall's DP approach handles negative edges (just not negative cycles) that this Dijkstra-based version cannot.",
      },
      {
        name: 'Optimized — Floyd-Warshall Dynamic Programming',
        explanation: 'Consider every node k as a possible "waypoint" between every pair (i, j): is going i → k → j shorter than the currently-known i → j distance? Trying every waypoint for every pair, in the right nested order, converges to the true shortest path between all pairs simultaneously.',
        steps: [
          'Start a distance matrix with direct edge weights (infinity where no direct edge exists, 0 on the diagonal).',
          'For each candidate waypoint k, for each pair (i, j): check if dist[i][k] + dist[k][j] beats the current dist[i][j].',
          'If so, update dist[i][j] — going through k is a shortcut.',
          'After trying every node as a waypoint, dist[i][j] holds the true shortest path for every pair at once.',
        ],
        visual: {
          arrays: [
            { label: 'dist row 0 (0→0,1,2)', values: [0, 3, 8] },
            { label: 'dist row 1 (1→0,1,2)', values: ['∞', 0, 1] },
            { label: 'dist row 2 (2→0,1,2)', values: ['∞', '∞', 0] },
          ],
          varNames: [],
          steps: [
            { note: 'Initial: direct edges only. dist[0][2] = 8 (the direct edge), even though a shorter route might exist through node 1.' },
            { highlights: { 'dist row 0 (0→0,1,2)': [1] }, note: 'Try waypoint k=1: check dist[0][2] vs dist[0][1] + dist[1][2] = 3 + 1 = 4. 4 < 8 — shortcut found!' },
            { snapshots: { 'dist row 0 (0→0,1,2)': [0, 3, 4] }, note: 'Update dist[0][2] = 4. This single check (repeated for every (i,j,k) triple) is the entire algorithm — no priority queue, no per-source traversal.' },
          ],
        },
        code:
`int[][] floydWarshall(int n, int[][] edges) {
    int[][] dist = new int[n][n];
    for (int[] row : dist) Arrays.fill(row, Integer.MAX_VALUE / 2);   // avoid overflow when adding
    for (int i = 0; i < n; i++) dist[i][i] = 0;
    for (int[] edge : edges) dist[edge[0]][edge[1]] = edge[2];

    for (int k = 0; k < n; k++) {          // try every waypoint
        for (int i = 0; i < n; i++) {
            for (int j = 0; j < n; j++) {
                if (dist[i][k] + dist[k][j] < dist[i][j]) {
                    dist[i][j] = dist[i][k] + dist[k][j];
                }
            }
        }
    }
    return dist;
}`,
        complexity: { time: 'O(V³)', space: 'O(V²)' },
        interviewNote: "The order of the loops matters critically: k must be the OUTERMOST loop. Trying every (i,j) fully before moving to the next k is what guarantees each dist[i][j] has considered every possible waypoint by the time the algorithm finishes.",
        whenToUse: "Floyd-Warshall is the standard choice specifically for ALL-PAIRS shortest paths on a graph small enough for V³ to be acceptable (a few hundred nodes) — especially when negative edge weights are present, which rules out Dijkstra entirely.",
      },
    ],
  },
  {
    id: 'detect-negative-cycle-bellman-ford',
    title: 'Detect a Negative Cycle',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Relax Until Nothing Changes (No Fixed Bound)',
        explanation: 'Keep relaxing every edge, repeating full passes, until a pass produces no changes at all. This works correctly for a graph with NO negative cycle — but if one exists, distances keep improving forever, and this loop never naturally terminates.',
        steps: [
          'Repeat: do a full pass relaxing every edge.',
          'If any distance improved during this pass, do another full pass.',
          'Stop only when a pass makes zero changes.',
          'If a negative cycle exists, distances along it keep improving every single pass — this never stops on its own.',
        ],
        code:
`// Risky: no iteration cap — loops forever if a negative cycle exists.
int[] relaxUntilStableBasic(int n, int[][] edges, int src) {
    int[] dist = new int[n];
    Arrays.fill(dist, Integer.MAX_VALUE);
    dist[src] = 0;

    boolean changed = true;
    while (changed) {   // no guaranteed termination
        changed = false;
        for (int[] edge : edges) {
            if (dist[edge[0]] != Integer.MAX_VALUE && dist[edge[0]] + edge[2] < dist[edge[1]]) {
                dist[edge[1]] = dist[edge[0]] + edge[2];
                changed = true;
            }
        }
    }
    return dist;
}`,
        complexity: { time: 'Unbounded — infinite if a negative cycle exists', space: 'O(V)' },
      },
      {
        name: 'Optimized — Exactly V Rounds (V−1 Relax, 1 Check)',
        explanation: 'A shortest path in a graph with V nodes can have at most V−1 edges (visiting each node once). So V−1 rounds of relaxation are always ENOUGH for a cycle-free graph — and if a Vth round STILL finds an improvement, that\'s only possible because of a negative cycle. This guarantees termination either way.',
        steps: [
          'Run exactly V−1 rounds of full-edge relaxation — this always suffices when there\'s no negative cycle.',
          'Run one MORE round (the Vth).',
          'If anything still improves in this extra round, a negative cycle exists — report it.',
          'If nothing improves, the distances from the first V−1 rounds are already final and correct.',
        ],
        code:
`boolean hasNegativeCycle(int n, int[][] edges, int src) {
    int[] dist = new int[n];
    Arrays.fill(dist, Integer.MAX_VALUE);
    dist[src] = 0;

    for (int round = 0; round < n - 1; round++) {
        for (int[] edge : edges) {
            if (dist[edge[0]] != Integer.MAX_VALUE && dist[edge[0]] + edge[2] < dist[edge[1]]) {
                dist[edge[1]] = dist[edge[0]] + edge[2];
            }
        }
    }

    for (int[] edge : edges) {   // the Vth, "check" round
        if (dist[edge[0]] != Integer.MAX_VALUE && dist[edge[0]] + edge[2] < dist[edge[1]]) {
            return true;   // still improving — negative cycle
        }
    }
    return false;
}`,
        complexity: { time: 'O(V · E)', space: 'O(V)' },
        whenToUse: "This exact V-rounds-then-check structure is what powers the \"Detect a negative cycle\" checkbox in this pattern's Bellman-Ford playground mode — always bound the relaxation loop by V, never by \"until nothing changes.\"",
      },
    ],
  },
];

export default advancedGraphDrills;
